/**
 * Fun Challenges Lobby route.
 *
 * Reachable from PlayChess → "Fun Challenges" tile in `GameSearch`. Phase 4
 * replaces the Phase 1 placeholder with the real lobby (game-type tile,
 * opponent picker, challenge settings).
 *
 * Side-effect imports `@/Screens/FunChallenges/adapters` so every battle adapter is
 * registered before the user picks a battle type.
 *
 * 2026-05-20 bug 3: if the user lands here while a fun challenge battle
 * is still active (e.g. they navigated away via the side menu and then
 * tapped Fun Challenges again), bounce them back to FunChallengeBattle.
 * The existing header pill ("Challenge in progress") already covers the
 * passive case, but the menu tap should reuse the live battle, not let
 * the user start a second one.
 */

import React, {useEffect} from 'react';
import {ActivityIndicator, StyleSheet, View} from 'react-native';

import {useNavigation} from '@react-navigation/native';

import CCText from '@/Atoms/CCText';
import {FontSizes} from '@/Utils/Dimensions';
import BattleLobby from './components/BattleLobby';
import useFunChallengesBattleStore from '@/Store/funChallengesBattleStore';
import useFunChallengesRoyaleStore from '@/Store/funChallengesRoyaleStore';
import {ScreenName} from '@/navigators/StackConstants';
import {useSocketReconnect} from '@/Hooks/UseSocket';
import ConnectionModal from '@/Screens/PlayChess/components/ConnectionModal';
import ConnectionNotice from './components/ConnectionNotice';

import '@/Screens/FunChallenges/adapters';

export default function FunChallengesLobby(props: {
  // Threaded down from Application.tsx like every drawer screen — drives the
  // shared ConnectionModal so the lobby can reclaim the socket / re-auth.
  changeClient?: boolean;
  setChangeClient?: (val: boolean) => void;
  authComplete?: boolean;
}) {
  const {changeClient, setChangeClient, authComplete = true} = props;
  const navigation = useNavigation<any>();
  const reconnectSocket = useSocketReconnect();
  const status = useFunChallengesBattleStore(s => s.status);
  const battleId = useFunChallengesBattleStore(s => s.session?.battleId);

  useEffect(() => {
    if (status !== 'in_battle' || !battleId) return;
    try {
      navigation.navigate(ScreenName.FunChallengeBattle);
    } catch {
      // Navigation may not be ready in some auth states — non-fatal.
    }
  }, [status, battleId, navigation]);

  // Same for Puzzle Royale: if a royale lobby/race is already active and the
  // user opens this lobby route (e.g. via the side menu mid-race), bounce them
  // back into PuzzleRoyaleBattle rather than letting them start a second one.
  // Auto-create / auto-join open this route from an IDLE store, so this never
  // pre-empts those flows.
  const royaleStatus = useFunChallengesRoyaleStore(s => s.status);
  const royaleRoomId = useFunChallengesRoyaleStore(
    s => s.lobby?.roomId ?? s.session?.roomId ?? null,
  );
  useEffect(() => {
    if (
      (royaleStatus !== 'lobby' && royaleStatus !== 'in_battle') ||
      !royaleRoomId
    ) {
      return;
    }
    try {
      navigation.navigate(ScreenName.PuzzleRoyaleBattle);
    } catch {
      // non-fatal
    }
  }, [royaleStatus, royaleRoomId, navigation]);

  // Deep-link cold open (?join=<code>): the socket authenticates a beat after
  // mount, so `authComplete` is briefly false — which would flash the scary
  // "Auth Failed" ConnectionModal. Instead, for an initial GRACE window we
  // suppress that modal and let the lobby panel's CCLoader show ("Reconnecting
  // you to the race…"). Only after the grace expires WITHOUT auth completing do
  // we treat it as a real failure and show the modal. A client-takeover
  // (changeClient) is always a real interruption → never suppressed.
  const isDeepLinkJoin =
    typeof window !== 'undefined' &&
    typeof window.location?.search === 'string' &&
    /[?&]join=/.test(window.location.search);
  // NOTE: do NOT cancel the grace when `authComplete` is true — it DEFAULTS to
  // true at mount (before the socket connects), so an "auth complete → cancel"
  // effect would disable suppression immediately, then the connect flips it
  // false and the modal flashes (the bug we're fixing). We don't need to cancel
  // on completion anyway: once authed, `!authComplete` is false so the modal is
  // already hidden. The 8s timer only matters if auth NEVER completes.
  const [graceElapsed, setGraceElapsed] = React.useState(false);
  useEffect(() => {
    if (!isDeepLinkJoin) {
      setGraceElapsed(true);
      return;
    }
    const id = setTimeout(() => setGraceElapsed(true), 8000);
    return () => clearTimeout(id);
  }, [isDeepLinkJoin]);

  const suppressAuthModal = isDeepLinkJoin && !authComplete && !graceElapsed;
  const showConnectionModal =
    !!changeClient || (!authComplete && !suppressAuthModal);

  // While we're auto-reconnecting an already-authenticated user who opened a
  // join link, cover the CONTENT AREA with a clean loader (the sidebar/header
  // stay) instead of letting them see the half-built lobby. Unauthenticated
  // users never reach here — the app redirects them to login. We only show
  // this during the suppression window (before the modal would take over).
  const showConnectingLoader = isDeepLinkJoin && !authComplete && !graceElapsed;

  return (
    <>
      {/* The lobby route is wrapped in a ScrollView by the navigator (unlike
          FunChallengeBattle / PlayChess, which use a flex View). The modal's
          own `position:absolute; height:100%` collapses inside a scroll
          container, so wrap it in a viewport-fixed layer so it covers the
          screen the same way it does on PlayChess. */}
      {showConnectionModal && (
        <View
          style={
            {
              position: 'fixed' as any,
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
            } as any
          }>
          <ConnectionModal
            visible
            onContinue={() => {
              setChangeClient?.(false);
              reconnectSocket();
            }}
            onClose={() => navigation.navigate('Dashboard')}
            authFail={!authComplete}
          />
        </View>
      )}
      {/* Socket-level disconnect banner (e.g. when the app takes over the
          shared socket). Parity with PlayChess's NotificationBox. */}
      <ConnectionNotice />
      <BattleLobby />

      {/* Content-area connecting loader for deep-link joins. Fixed to the
          viewport but inset from the left so the app sidebar stays visible;
          covers the lobby content until the socket reconnects (auth confirms). */}
      {showConnectingLoader && (
        <View style={styles.connectingOverlay}>
          <ActivityIndicator size="large" color="#1F6C63" />
          <CCText style={styles.connectingTitle}>
            Reconnecting you to the race…
          </CCText>
          <CCText style={styles.connectingHint}>
            Almost there — you'll be able to join or spectate in a moment.
          </CCText>
        </View>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  // Fixed to the viewport; left inset clears the app sidebar (~96px) so it
  // covers only the content area. Solid cream so the lobby chrome is hidden.
  connectingOverlay: {
    position: 'fixed' as any,
    top: 0,
    bottom: 0,
    right: 0,
    left: 96,
    zIndex: 900,
    backgroundColor: '#FAF6EB',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 24,
  },
  connectingTitle: {
    fontSize: FontSizes[18] || 18,
    fontWeight: '700',
    color: '#4D3F37',
    textAlign: 'center',
  },
  connectingHint: {
    fontSize: FontSizes[13] || 13,
    color: '#8A7E5F',
    textAlign: 'center',
  },
});
